import { Alert } from "@package/subframe/components/Alert";
import { Button } from "@package/subframe/components/Button";
import { FeatherDownload, FeatherKey, FeatherPlus } from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { PeopleDirectoryCards } from "@/components/people/directory/PeopleDirectoryCards";
import { PeopleDirectoryFilters } from "@/components/people/directory/PeopleDirectoryFilters";
import { PeopleDirectoryTable } from "@/components/people/directory/PeopleDirectoryTable";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { ResetPasswordDialog } from "@/features/account-actions/ResetPasswordDialog";
import { useInvalidateAccount } from "@/features/account-actions/use-invalidate-account";
import { BulkSetPinDialog } from "@/features/staff/BulkSetPinDialog";
import type { EmployeeDirectoryEntryDto } from "@/generated/model/employeeDirectoryEntryDto";
import { useListEmployeeDirectory } from "@/generated/people/people";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useIsMobile } from "@/hooks/use-mobile";
import type { SortDir } from "@/lib/category-display";
import { downloadBlob } from "@/lib/download";
import { isProfileUnavailable } from "@/lib/people";
import {
  BULK_PIN_LIMIT,
  directoryCsv,
  directoryCsvFilename,
  type DirectoryFilters,
  type DirectorySortColumn,
  employeeName,
  filterEmployees,
  hasActiveFilters,
  jobTitleOptions,
  NO_DIRECTORY_FILTERS,
  roomOptions,
  sortEmployees,
} from "@/lib/people-directory";
import { staffUsername } from "@/lib/staff-username";

const SEARCH_DEBOUNCE_MS = 250;

/**
 * AC1-AC6: everyone at the selected nursery. The API decides who may see it
 * and which columns they get; the browser filters, searches and sorts.
 */
export function PeopleDirectoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedNurseryId, selectedNursery } = useNursery();
  const invalidateAccount = useInvalidateAccount();
  const isMobile = useIsMobile();
  const directoryQuery = useListEmployeeDirectory(selectedNurseryId ?? "", {
    query: {
      enabled: !!selectedNurseryId,
      refetchOnMount: "always",
      // Not kept after the page unmounts: the next person on a shared device
      // must not see the list flash up from cache.
      gcTime: 0,
    },
  });
  const directory = directoryQuery.data?.data;

  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [filters, setFilters] =
    useState<DirectoryFilters>(NO_DIRECTORY_FILTERS);
  const [sortColumn, setSortColumn] = useState<DirectorySortColumn>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPinOpen, setBulkPinOpen] = useState(false);
  const [resetTarget, setResetTarget] =
    useState<EmployeeDirectoryEntryDto | null>(null);

  // A practitioner or leader only has their own profile (AC29).
  const isUnavailable =
    directoryQuery.isError && isProfileUnavailable(directoryQuery.error);
  useEffect(() => {
    if (!isUnavailable) return;
    if (!user) return;
    navigate({
      to: "/people/$userId",
      params: { userId: user.id },
      replace: true,
    });
  }, [isUnavailable, user, navigate]);

  const employees = useMemo(() => directory?.employees ?? [], [directory]);
  const visible = useMemo(
    () =>
      sortEmployees(
        filterEmployees(employees, filters, search),
        sortColumn,
        sortDir,
      ),
    [employees, filters, search, sortColumn, sortDir],
  );
  const rooms = useMemo(() => roomOptions(employees), [employees]);
  const jobTitles = useMemo(() => jobTitleOptions(employees), [employees]);

  if (!selectedNurseryId) {
    return (
      <PageShell>
        <Alert variant="neutral" title="Select a nursery to see its people" />
      </PageShell>
    );
  }

  if (directoryQuery.isPaused && !directory) {
    return (
      <PageShell>
        <Alert
          variant="neutral"
          title="People Hub needs a connection"
          description="Reconnect to see your team."
        />
      </PageShell>
    );
  }

  // A failed background refresh keeps showing the list it already has.
  if (directoryQuery.isError && !isUnavailable && !directory) {
    return (
      <PageShell>
        <Alert
          variant="error"
          title="Could not load your team"
          description="Try again in a moment."
        />
      </PageShell>
    );
  }

  if (!directory) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const { capabilities } = directory;
  const selectable = visible.filter((employee) => employee.canManageLogin);
  const firstSelectable = selectable.slice(0, BULK_PIN_LIMIT);
  // Only people still on screen are counted and sent: a filter can hide some
  // of a selection, and they come back selected when it is cleared.
  const selectedUserIds = selectable
    .filter((employee) => selectedIds.has(employee.userId))
    .map((employee) => employee.userId);
  const canClear = hasActiveFilters(filters, searchInput);

  function sortBy(column: DirectorySortColumn) {
    if (column === sortColumn) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDir("asc");
  }

  function toggle(userId: string, checked: boolean) {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  }

  function clearFilters() {
    setSearchInput("");
    setFilters(NO_DIRECTORY_FILTERS);
  }

  function exportList() {
    const csv = directoryCsv(visible, {
      includeLogin: capabilities.canViewLogin,
    });
    downloadBlob(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
      directoryCsvFilename(selectedNursery?.name ?? "nursery"),
    );
  }

  const openProfile = (userId: string) =>
    navigate({ to: "/people/$userId", params: { userId } });

  return (
    <PageShell testId="people-directory">
      <div className="flex w-full flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            People Hub
          </span>
          <span className="text-body font-body text-subtext-color">
            Your complete team at a glance.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="neutral-secondary"
            icon={<FeatherDownload />}
            disabled={visible.length === 0}
            onClick={exportList}
          >
            Export list
          </Button>
          {capabilities.canAddEmployee && (
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={() => navigate({ to: "/people/new" })}
            >
              Add Employee
            </Button>
          )}
        </div>
      </div>

      <PeopleDirectoryFilters
        search={searchInput}
        onSearchChange={setSearchInput}
        filters={filters}
        onFiltersChange={setFilters}
        rooms={rooms}
        jobTitles={jobTitles}
        showAccountFilter={capabilities.canViewLogin}
        canClear={canClear}
        onClear={clearFilters}
      />

      {directory.truncated && (
        <Alert
          variant="warning"
          title={`Showing the first ${employees.length} of ${directory.total} employees by surname`}
          description="Search, filters and export only cover these."
        />
      )}

      {selectedUserIds.length > 0 && (
        <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md bg-secondary-beige-50 px-4 py-3">
          <span className="text-body font-body text-default-font">
            {selectedUserIds.length} selected
            {selectedUserIds.length >= BULK_PIN_LIMIT &&
              ` (the most you can set at once)`}
          </span>
          <div className="flex items-center gap-3">
            <Button
              variant="neutral-secondary"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear selection
            </Button>
            <Button
              variant="brand-primary"
              icon={<FeatherKey />}
              onClick={() => setBulkPinOpen(true)}
            >
              Set temporary PIN
            </Button>
          </div>
        </div>
      )}

      {employees.length === 0 ? (
        <EmptyState title="No one works here yet">
          {capabilities.canAddEmployee
            ? "Add your first employee to get started."
            : null}
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState title="No one matches those filters">
          <span>
            {search.trim() ? `Nothing found for “${search.trim()}”. ` : null}
            Try clearing the search or widening a filter.
          </span>
          <Button variant="neutral-secondary" onClick={clearFilters}>
            Clear all filters
          </Button>
        </EmptyState>
      ) : (
        <>
          {isMobile ? (
            <PeopleDirectoryCards
              employees={visible}
              showLogin={capabilities.canViewLogin}
            />
          ) : (
            <PeopleDirectoryTable
              employees={visible}
              sortColumn={sortColumn}
              sortDir={sortDir}
              onSort={sortBy}
              showLogin={capabilities.canViewLogin}
              showActions={capabilities.canManageLogins}
              selection={
                capabilities.canManageLogins
                  ? {
                      selectedIds,
                      allSelected:
                        firstSelectable.length > 0 &&
                        firstSelectable.every((employee) =>
                          selectedIds.has(employee.userId),
                        ),
                      noneSelectable: firstSelectable.length === 0,
                      atLimit: selectedUserIds.length >= BULK_PIN_LIMIT,
                      onToggle: toggle,
                      onToggleAll: (checked) =>
                        setSelectedIds(
                          checked
                            ? new Set(
                                firstSelectable.map(
                                  (employee) => employee.userId,
                                ),
                              )
                            : new Set(),
                        ),
                    }
                  : null
              }
              onOpen={openProfile}
              onResetPassword={setResetTarget}
            />
          )}
          <span className="text-caption font-caption text-subtext-color">
            Showing {visible.length} of {employees.length} employees
          </span>
        </>
      )}

      <BulkSetPinDialog
        userIds={selectedUserIds}
        open={bulkPinOpen}
        onOpenChange={setBulkPinOpen}
        onSuccess={() => {
          invalidateAccount(selectedUserIds);
          setSelectedIds(new Set());
        }}
      />
      {resetTarget && (
        <ResetPasswordDialog
          key={resetTarget.userId}
          userId={resetTarget.userId}
          displayName={employeeName(resetTarget)}
          username={staffUsername(resetTarget.email)}
          open
          onOpenChange={(open) => {
            if (!open) setResetTarget(null);
          }}
        />
      )}
    </PageShell>
  );
}

function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-3 rounded-md border border-dashed border-neutral-border px-6 py-12 text-center">
      <span className="text-body-bold font-body-bold text-default-font">
        {title}
      </span>
      <div className="flex flex-col items-center gap-3 text-body font-body text-subtext-color">
        {children}
      </div>
    </div>
  );
}

function PageShell({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex w-full flex-col gap-6 px-4 py-6 animate-fade-in"
    >
      {children}
    </div>
  );
}
